import React, { useEffect, useState, useRef } from 'react';
import { Col, Row, Container, Input } from 'reactstrap';
import { useParams, Link, useHistory } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { get } from 'lodash-es';
import ReactLoading from 'react-loading';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import ReactToPrint, { useReactToPrint } from 'react-to-print';

import { useAxios } from '../../../hooks';
import { selectHouseRef, selectPolicyID } from '../../../selectors';
import { setInvoiceSendData } from '../../../modules/invoice';
import ViewInvoice from './ViewInvoice';
import PrintableInvoicePreview from './PrintableInvoicePreview';
import InvaluableShippingBanner from '../InvaluableShippingBanner/InvaluableShippingBanner';

const InvoiceGroupPreview = () => {
  const { invoiceGroupID, invoiceIDList } = useParams();
  const invoiceIDStringArray = invoiceIDList.split(',');
  const [invoiceIDArray, setInvoiceIDArray] = useState(invoiceIDStringArray.map(Number));
  const houseRef = useSelector(selectHouseRef);

  // check if there is a policyID => Invaluable Shipping is available
  const isInvShippingAvailable = useSelector(selectPolicyID);

  const history = useHistory();
  const dispatch = useDispatch();
  const {
    data,
    isIdle: isInvoicePreviewIdle,
    isLoading: isInvoicePreviewLoading,
  } = useAxios(
    () => `/app/house/${houseRef}/invoiceGroup/${invoiceGroupID}/invoicePreview`,
    [houseRef, invoiceGroupID],
    {
      method: 'post',
      data: invoiceIDArray,
      headers: { 'Content-Type': 'application/json' },
    }
  );
  const {
    exec: execInvoiceSend,
    isLoading: isInvoiceSendLoading,
    data: invoiceSendData,
  } = useAxios(
    () => `/app/house/${houseRef}/invoiceGroup/${invoiceGroupID}/invoiceSend`,
    [houseRef, invoiceGroupID, invoiceIDList],
    { method: 'post', data: invoiceIDArray },
    { manual: true }
  );

  const [jumpToValue, setJumpToValue] = useState('');
  const [invoiceArrayIndex, setInvoiceArrayIndex] = useState(0);
  const [showPrevLink, setShowPrevLink] = useState(false);
  const [showNextLink, setShowNextLink] = useState(true);
  const [invoiceGroupsData, setInvoiceGroupsData] = useState([]);
  const [isJumpToError, setIsJumpToError] = useState(false);
  const [viewAll, setViewAll] = useState(false);
  const [fromCreateInvoices, setFromCreateInvoices] = useState(false);
  const [fromEditInvoices, setFromEditInvoices] = useState(false);
  const [topBarName, setTopBarName] = useState('');
  const printRef = useRef();
  const showSpinner = isInvoicePreviewIdle || isInvoicePreviewLoading || isInvoiceSendLoading;

  useEffect(() => {
    if (data) {
      const groupsData = get(data, 'data.data.invoiceGroups', []);
      setInvoiceGroupsData(groupsData);
      //  correct the url-based invoice ID array to match the order in the data
      const actualIDArray = [];
      groupsData.forEach((group) => {
        actualIDArray.push(Number(group.invoices.invoiceList[0].invoiceID));
      });
      setInvoiceIDArray(actualIDArray);
    }
  }, [data]);

  useEffect(() => {
    setShowPrevLink(invoiceArrayIndex > 0);
    setShowNextLink(invoiceArrayIndex < invoiceIDArray.length - 1);
  }, [invoiceArrayIndex]);

  useEffect(() => {
    if (invoiceGroupsData.length) {
      setTopBarName(`${invoiceGroupsData[invoiceArrayIndex].invoices.invoiceList[0].billing.lastName}, ${invoiceGroupsData[invoiceArrayIndex].invoices.invoiceList[0].billing.firstName}`);
    }
  }, [invoiceGroupsData, invoiceArrayIndex]);

  useEffect(() => {
    if (invoiceSendData) {
      const invoiceSendResponseData = get(invoiceSendData, 'data.data', {});

      if (invoiceSendResponseData.success === 'true') {
        dispatch(setInvoiceSendData(invoiceSendResponseData));
        history.push(`/invoices/SentInvoices/${invoiceGroupID}/${invoiceIDList}`);
      }
    }
  }, [invoiceSendData]);

  useEffect(() => {
    const sfciFlag = get(window, 'sessionStorage.fromCreateInvoices', '');
    const fromEditInvoiceFlag = get(window, 'sessionStorage.fromEditInvoices', '');
    if (sfciFlag) {
      setFromCreateInvoices(sfciFlag === 'true');
    }
    if (fromEditInvoiceFlag) {
      setFromEditInvoices(fromEditInvoiceFlag === 'true');
    }
  }, []);

  const incrementArrayIndex = () => {
    setInvoiceArrayIndex(invoiceArrayIndex + 1);
  };

  const decrementArrayIndex = () => {
    setInvoiceArrayIndex(invoiceArrayIndex - 1);
  };

  const handleJumpToChange = (value) => {
    setJumpToValue(value);
    setIsJumpToError(false);
  };

  const handleJumpToClick = () => {
    if (jumpToValue) {
      const getIndex = invoiceIDArray.indexOf(Number(jumpToValue));
      if (getIndex === -1) {
        setIsJumpToError(true);
      } else {
        setViewAll(false);
        setInvoiceArrayIndex(getIndex);
        setJumpToValue('');
      }
    }
  };

  const handleSendInvoices = () => {
    execInvoiceSend();
  };

  const checkForEnterKey = (e) => {
    if (e.keyCode === 13) {
      handleJumpToClick();
    }
  };

  const handlePrint = useReactToPrint({
    content: () => printRef.current,
  });

  if (showSpinner) {
    return (
      <ReactLoading className="loading-spinner" type="spin" color="black" />
    );
  }

  return (
    <Container>
      <Row className="mt-4">
        <Col xs={12}>
          {fromCreateInvoices && (
            <Link
              aria-label="link to existing"
              className=""
              to={`/invoices/CreateNewInvoices/${invoiceGroupID}`}
            >
              <FontAwesomeIcon icon="angle-left" className="fa-solid color-white-bg" />  Back to Create New Invoices
            </Link>
          )}
          {!fromCreateInvoices && (
            <Link
              aria-label="link to group details"
              className=""
              to={`/invoices/EditInvoices/${invoiceGroupID}`}
            >
              <FontAwesomeIcon icon="angle-left" className="fa-solid color-white-bg" /> Back to Edit Invoices
            </Link>
          )}
        </Col>
      </Row>
      {!!isInvShippingAvailable && <InvaluableShippingBanner />}
      <Row className="mt-3">
        <Col xs={6} className="pt-3">
          <h1 className="">
            Invoice Preview{invoiceIDArray.length > 1 && 's'}
          </h1>
        </Col>
        <Col xs={6} className="text-right">
          <div className="float-right">
            <button
              className="btn btn-primary"
              aria-label="send all"
              type="button"
              onClick={() => handleSendInvoices()}
            >
              Send All
            </button>
          </div>
          <div className="float-right mr-4">
            <ReactToPrint
              documentTitle="InvaluableAuctions - invoice"
              trigger={() => (
                <button
                  className="btn btn-link"
                  aria-label="print"
                  type="button"
                  onClick={() => handlePrint()}
                >
                  <FontAwesomeIcon icon="print" className="color-white-bg" /> Print {viewAll && ('All')}
                </button>
              )}
              content={() => printRef.current}
            />
          </div>
        </Col>
      </Row>
      <Row className="border mt-2" style={{ backgroundColor: '#f1f1f1' }}>
        <Col xs={3}>
          <div className="font-weight-bold mt-4 ml-2 pb-3">
            {topBarName}
          </div>
        </Col>
        <Col xs={6} className="py-3 text-center">
          {invoiceIDArray.length > 1 && (
            <>
              <div className="d-inline-block position-relative">
                <Input
                  id="jumpTo"
                  name="jumpTo"
                  type="text"
                  placeholder="Jump to invoice #"
                  onChange={(e) => handleJumpToChange(e.target.value)}
                  onKeyUp={(e) => checkForEnterKey(e)}
                  value={jumpToValue}
                  className={
                  isJumpToError ? ' width-200 error-border' : 'width-200'
                }
                />
                {isJumpToError && (
                <span id="jumpToErrorBlock" className="jump-to-error">
                  Invalid invoice #
                </span>
                )}
              </div>
              <div className="d-inline-block ml-2">
                <button
                  className="btn gray-button"
                  aria-label="jump to button"
                  type="button"
                  onClick={() => handleJumpToClick()}
                >
                  Go
                </button>
              </div>
            </>
          )}
          {invoiceIDArray.length > 1 && (
            <>
              <div className="d-inline-block ml-4">
                {!viewAll && (
                  <button
                    className="btn btn-link"
                    aria-label="view all"
                    type="button"
                    onClick={() => setViewAll(true)}
                  >
                    <span className="p-2">View All</span>
                  </button>
                )}
                {viewAll && (
                  <button
                    className="btn btn-link"
                    aria-label="view one"
                    type="button"
                    onClick={() => setViewAll(false)}
                  >
                    <span className="p-2">View One</span>
                  </button>
                )}
              </div>
            </>
          )}
        </Col>
        <Col xs={3} className="text-right py-3">
          {showNextLink && !viewAll && (
            <div className="float-right display-flex">
              <button
                className="btn btn-link display-flex"
                aria-label="next"
                type="button"
                style={{ display: 'flex' }}
                onClick={() => incrementArrayIndex()}
              >
                Next <FontAwesomeIcon icon="angle-right" className="fa-solid color-white-bg ml-1 mt-1" />
              </button>
            </div>
          )}
          {showPrevLink && !viewAll && (
            <div className="float-right mr-4">
              <button
                className="btn btn-link"
                aria-label="previous"
                type="button"
                onClick={() => decrementArrayIndex()}
              >
                <FontAwesomeIcon icon="angle-left" className="fa-solid color-white-bg" /> Prev
              </button>
            </div>
          )}
        </Col>
      </Row>
      {invoiceGroupsData[invoiceArrayIndex] && !viewAll && (
        <Row>
          <Col xs={12}>
            <ViewInvoice
              invoiceID={invoiceIDArray[invoiceArrayIndex]}
              invoiceData={invoiceGroupsData[invoiceArrayIndex]}
              isInvShippingAvailable={!!isInvShippingAvailable}
            />
          </Col>
        </Row>
      )}
      {invoiceGroupsData.length && viewAll && (
        <Row>
          {invoiceIDArray.map((id, index) => (
            <Col xs={12} key={id}>
              <ViewInvoice
                invoiceID={invoiceIDArray[index]}
                invoiceData={invoiceGroupsData[index]}
                isInvShippingAvailable={!!isInvShippingAvailable}
              />
            </Col>
          ))}
        </Row>
      )}
      {/* printable invoice container */}
      {invoiceGroupsData.length && (
      <div className="d-none">
        <PrintableInvoicePreview
          ref={printRef}
          invoiceIDArray={invoiceIDArray}
          invoiceArrayIndex={invoiceArrayIndex}
          invoiceGroupsData={invoiceGroupsData}
          viewAll={viewAll}
        />
      </div>
      )}

    </Container>
  );
};

export default InvoiceGroupPreview;
